/**
 * 「見張り役」のカード・凍結中の帯・「止める」ボタン（ADR-0052、Phase 3-5）の中身。DOM を持たない部分（テストできるように分ける）。
 * 何を出すかは Gateway が DB の行からコードで作った値（GET /v1/monitor）だけで決める。文はここで組み立てるが、事実（時刻・規則・数）は全て応答から。
 * 凍結を解くボタンは無い（D5。解くのは MacBook の `pnpm jarvis unfreeze` だけ）。
 */
export interface MonitorAlertItem {
  id: string;
  at: string;
  severity: "info" | "warn" | "critical";
  rule: string;
  label: string;
  action: "log" | "block" | "freeze";
  detail: string | null;
  until: string | null;
  trace_id: string | null;
}

export interface MonitorView {
  frozen: {
    since: string;
    rule: string;
    label: string;
    detail: string | null;
    by: string | null;
    trace_id: string | null;
  } | null;
  monitor: {
    state: "ok" | "down" | "never" | "unknown";
    last_tick_ago_sec: number | null;
    stale_after_sec: number;
    failed_ticks_24h: number | null;
  };
  alerts: MonitorAlertItem[];
  more: number;
  blocked: string[] | null;
  alerts_hours: number;
  refresh_sec: number;
  reason_max_chars: number;
  /** このセッションから止められるか（パスキーの端末だけ。開発用トークンは見るだけ） */
  can_freeze?: boolean;
}

export function ago(sec: number): string {
  if (sec < 90) return "さっき";
  if (sec < 3600) return `${Math.round(sec / 60)} 分前`;
  if (sec < 86_400) return `${Math.round(sec / 3600)} 時間前`;
  return `${Math.round(sec / 86_400)} 日前`;
}

/** 解き方の案内（凍結中の帯）。手順の正は docs/runbook/monitor.md「凍結を解く」 */
export const UNFREEZE_HELP =
  '会話と読み取りは続きます。書き込み・外への送信・作業係は止まっています。解くのは本人だけで、MacBook で pnpm jarvis unfreeze "<何を確かめたか>"（本人の鍵で署名）。';

export interface FrozenBanner {
  title: string;
  reason: string;
  help: string;
}

export function frozenBanner(v: MonitorView): FrozenBanner | null {
  const f = v.frozen;
  if (!f) return null;
  const who =
    f.by === "pwa"
      ? "（アプリの「止める」）"
      : f.by === "cli"
        ? "（MacBook の pnpm jarvis freeze）"
        : "";
  return {
    title: "Jarvis を止めています（凍結中）",
    reason: `${f.since} から。${f.label}${who}${f.detail ? `: ${f.detail}` : ""}`,
    help: UNFREEZE_HELP,
  };
}

/** 見張り役の生存の 1 行（正は時刻。Gateway が最後の見回りと今の差を返す） */
export function monitorLine(v: MonitorView): { text: string; down: boolean } {
  const m = v.monitor;
  if (m.state === "down")
    return {
      text: `見張り役が ${ago(m.last_tick_ago_sec ?? 0)}から見回っていません（MacBook が眠っているか、見張り役が止まっています）`,
      down: true,
    };
  if (m.state === "never") return { text: "見張り役はまだ動いていません", down: false };
  if (m.state === "unknown") return { text: "見張り役の様子はまだ読めません", down: false };
  const failed = m.failed_ticks_24h
    ? `。この 24 時間に失敗した見回り ${m.failed_ticks_24h} 回`
    : "";
  const when = ago(m.last_tick_ago_sec ?? 0);
  const checked = when === "さっき" ? "さっき確認" : `${when}に確認`;
  return { text: `見張り役: 見回り中（${checked}${failed}）`, down: false };
}

const ACTION_LABEL: Record<MonitorAlertItem["action"], string> = {
  log: "記録",
  block: "止めた",
  freeze: "凍結",
};

/** 警告の 1 行 */
export function alertLine(a: MonitorAlertItem): string {
  const until = a.until ? `（${a.until} まで止めています）` : "";
  return `${a.at} ${a.label}［${ACTION_LABEL[a.action]}］${a.detail ? ` ${a.detail}` : ""}${until}`;
}

export interface MonitorCardModel {
  banner: FrozenBanner | null;
  status: { text: string; down: boolean };
  alerts: { item: MonitorAlertItem; text: string }[];
  /** 「ほか N 件」。無ければ null */
  moreLine: string | null;
  /** 「止めている能力: …」。無ければ null */
  blockedLine: string | null;
  /** 「止める」ボタンを出すか（凍結中は出さない。もう止まっている） */
  canFreeze: boolean;
  /** 開いている間に描き直す間隔（ミリ秒）。凍結中・見張り役が止まっている間だけ。ほかは null */
  refreshMs: number | null;
  /** 見出し（警告の時間） */
  heading: string;
}

export function monitorCardModel(v: MonitorView): MonitorCardModel {
  const status = monitorLine(v);
  const banner = frozenBanner(v);
  return {
    banner,
    status,
    alerts: v.alerts.map((item) => ({ item, text: alertLine(item) })),
    moreLine: v.more > 0 ? `ほか ${v.more} 件` : null,
    blockedLine: v.blocked?.length ? `止めている能力: ${v.blocked.join("、")}` : null,
    canFreeze: !!v.can_freeze && !banner,
    refreshMs: banner || status.down ? Math.max(5, v.refresh_sec) * 1000 : null,
    heading: v.alerts.length
      ? `見張り役（この ${v.alerts_hours} 時間の警告 ${v.alerts.length + v.more} 件）`
      : "見張り役",
  };
}

/** 「止める」の確認の文（押してから 1 回聞く） */
export const FREEZE_CONFIRM =
  "Jarvis を止めますか？\n書き込み・外への送信・作業係が止まります。会話と読み取りは続きます。\n解くのは MacBook の pnpm jarvis unfreeze だけです（アプリからは解けません）。";

/** 止められなかったときの文 */
export function freezeErrorText(code: string, max: number): string {
  const msg: Record<string, string> = {
    freeze_unavailable:
      'まだアプリからは止められません（権限の差分の前）。MacBook で pnpm jarvis freeze "<理由>" を使ってください。',
    passkey_session_required: "この端末からは止められません（パスキーで登録した端末だけ）。",
    voice_device_not_allowed: "声の端末からは止められません。",
    reason_too_long: `理由は ${max} 字までにしてください。`,
  };
  return msg[code] ?? `止められませんでした（${code}）。MacBook で pnpm jarvis freeze も使えます。`;
}
